<template>
  <div class="container py-14">
    <RouterLink
      :to="`/initiatives${
        global.resources?.filters ? `?filters=${global.resources.filters}` : ''
      }`"
      class="flex items-center mb-8"
    >
      <IconChevronLeft class="size-4 mt-0.5" />
      <span>Инициативы</span>
    </RouterLink>
    <LoadingView :loading="isLoading" :error="error" class="min-h-[70vh]">
      <div class="flex flex-wrap -mx-4">
        <div class="w-full md:w-7/12 lg:w-8/12 px-4 mb-4 md:mb-0">
          <LoadingView :loading="isLoading">
            <UIHeading :title="data.title" tag="1" class="mb-3" />
            <div class="flex items-start flex-wrap gap-4 justify-between mb-3">
              <div
                class="max-w-[550px] w-full text-xs text-black-600 dark:text-black-300 tracking-widest uppercase"
                v-text="data.type?.title"
              />
              <div class="flex items-center gap-2">
                <Reactions
                  :global="data.reactions.global"
                  :personal="data.reactions.personal"
                  position="left"
                  @set-reaction="
                    (val) =>
                      setReaction(
                        `/initiatives/${route.params.id}/react`,
                        val,
                        'initiatives',
                      )
                  "
                />
                <UISubscribe :subscribed="data.subscribed" @click="subscribe" />
              </div>
            </div>

            <div
              v-if="data.address?.title"
              class="flex items-start cursor-pointer mt-3"
              @click="
                callMapModal(
                  [
                    {
                      ...data.address,
                      latitude: data.address.latitude,
                      longitude: data.address.longitude,
                    },
                  ],
                  {
                    zoom: 15,
                    title: data.address.title,
                  },
                )
              "
            >
              <IconMapPin class="mr-2 w-6 h-6 text-primary dark:text-brand-1" />
              <span
                class="text-primary dark:text-brand-1"
                v-text="`${data.address.title} (показать на карте)`"
              />
            </div>
            <Gallery class="mt-8" :media="data.media" />

            <div v-html="data.description" :class="proseContent" />

            <Gallery
              class="mt-2 mb-12"
              :media="data.media"
              :collections="['videos']"
            />

            <div class="grid md:grid-cols-2 my-8">
              <div class="flex flex-col">
                <span
                  class="text-xs mb-2 uppercase dark:text-white/50 text-black/50"
                  >Стоимость проекта</span
                ><span class="inline-flex items-center text-xl font-semibold"
                  >{{ numberWithCommas(data.cost) }}
                  <IconCurrencyRubel class="ml-1 w-5 h-5"
                /></span>
              </div>
              <div class="flex flex-col">
                <span
                  class="text-xs mb-2 uppercase dark:text-white/50 text-black/50"
                  >Сроки реализации</span
                ><span class="text-xl font-semibold">{{
                  getDateFormat(data.finishes_at)
                }}</span>
              </div>
            </div>

            <UIHeading title="Ожидаемые результаты" tag="3" class="mb-3" />

            <div v-html="data.expectation" :class="proseContent" />

            <div class="flex justify-between mt-6">
              <div>
                <a
                  class="text-primary dark:text-brand-1"
                  :href="data.source?.url"
                  target="_blank"
                  v-text="data.source?.title"
                />
              </div>
              <div v-if="data">
                <div>
                  <div
                    class="text-xs text-black-600 dark:text-black-300 tracking-widest uppercase mb-2"
                    v-text="'Поделиться'"
                  />

                  <ShareButtons :data="socialNetworkShareData" />
                </div>
              </div>
            </div>
          </LoadingView>
        </div>
        <div class="w-full md:w-5/12 lg:w-4/12 px-4 sm:pl-8">
          <div class="sticky top-24">
            <div :class="cardAside" class="!px-3 !py-4">
              <ul class="space-y-5">
                <li v-if="data.user">
                  <div
                    class="flex items-start"
                    :style="`color: ${data.status?.color}`"
                  >
                    <IconUser class="mr-2 size-5 text-black-400" />
                    <span v-text="data.user" />
                  </div>
                </li>

                <li v-if="data.status">
                  <div
                    class="flex items-center"
                    :style="`color: ${data.status?.color}`"
                  >
                    <IconSelect class="mr-2 size-5 text-black-400" />
                    <span v-text="data.status?.title" />
                  </div>
                </li>
                <li
                  v-if="data.vote_starts_at || data?.vote_finishes_at"
                  class="flex items-center"
                >
                  <IconCalendarClock class="mr-2 size-5 text-black-400" />
                  <ConditionalDisplaying
                    type="date"
                    format="D MMMM YYYY"
                    :from="data.vote_starts_at"
                    :to="data?.vote_finishes_at"
                    to-label="по"
                  />
                </li>
                <li v-if="data.protocols_count" class="flex items-center">
                  <IconChecks class="mr-2 w-6 h-6 text-black-400" />
                  <span
                    class="text-base"
                    v-text="
                      `${data.protocols_count} ${plural(data.protocols_count, [
                        'человек проголосовал',
                        'человека проголосовало',
                        'человек проголосовали',
                      ])}`
                    "
                  />
                </li>
              </ul>
            </div>
            <div
              v-if="
                dayjs(data.vote_starts_at).isBefore(new Date()) &&
                dayjs(data.vote_finishes_at).isAfter(new Date()) &&
                (data.can_vote || userHasProtocol)
              "
            >
              <div
                v-if="
                  !userHasProtocol &&
                  !dayjs(data.vote_finishes_at).isBefore(new Date())
                "
                :class="cardAside"
                class="my-6"
              >
                <InlineFormPlayer :form="data.form_id" :callback="store" />
              </div>

              <div
                v-else-if="userHasProtocol"
                :class="cardAside"
                class="mt-6 flex items-center"
              >
                <IconChecks class="mr-2 size-5" />
                <span>Вы поддержали этот проект</span>
              </div>
            </div>
            <div
              v-if="
                !userHasProtocol &&
                data.vote_issues?.length &&
                dayjs(data.vote_starts_at).isBefore(new Date()) &&
                dayjs(data.vote_finishes_at).isAfter(new Date())
              "
              :class="cardAside"
              class="my-6"
            >
              <p class="mb-2">Вы не можете принимать участие в голосовании:</p>
              <ul class="text-sm space-y-2 list-disc pl-4 text-rose-400">
                <li v-for="issue in data.vote_issues">
                  {{ issue.message }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="w-full md:w-7/12 lg:w-8/12 px-4">
          <Comments
            class="mt-12"
            :id="route.params.id"
            :dependant="!isLoading"
            root="initiatives"
            :endpoint="`/initiatives/${route.params.id}/comments`"
          />
        </div>
      </div>
    </LoadingView>
  </div>
  <UIFooter />
</template>

<script setup>
import dayjs from 'dayjs'
import { notify } from 'notiwind'
import { useSeoMeta } from 'unhead'
import { computed, ref, watch } from 'vue'
import { onBeforeRouteUpdate } from 'vue-router'

import {
  IconCalendarClock,
  IconChecks,
  IconChevronLeft,
  IconCurrencyRubel,
  IconMapPin,
  IconSelect,
  IconUser,
} from '@tabler/icons-vue'

import Comments from '@/components/comments/Comments.vue'
import ConditionalDisplaying from '@/components/common/ConditionalDisplaying.vue'
import Gallery from '@/components/common/Gallery.vue'
import LoadingView from '@/components/loading/LoadingView.vue'
import InlineFormPlayer from '@/components/player/InlineFormPlayer.vue'
import Reactions from '@/components/Reactions.vue'
import ShareButtons from '@/components/share/ShareButtons.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import UISubscribe from '@/components/ui/UISubscribe.vue'
import {
  callMapModal,
  getDateFormat,
  numberWithCommas,
  plural,
} from '@/utils/global.js'
import { getMediaPath } from '@/utils/media.js'
import { useReactions } from '@/utils/reactions.js'
import { cardAside, proseContent } from '@/utils/ui.js'
import { usePageId } from '@/utils/use/usePageId.js'

const {
  route,
  http,
  client,
  auth,
  global,
  pageState: { isLoading, isFetching, isError, data, error, refetch },
  subscribe,
} = usePageId({
  rootKey: 'initiatives',
  queryKey: 'initiative',
})

watch(
  () => isLoading.value,
  () => {
    userHasProtocol.value = data.value.has_protocol
  },
)

const userHasProtocol = ref(false)

const { setReaction } = useReactions({
  queryClient: client,
  auth,
  queryKey: ['initiative', route.params.id],
  rootKey: 'initiatives',
  rootParamId: route.params.id,
})

const socialNetworkShareData = computed(() => {
  const path = new URL(location.href)
  if (auth.user) {
    path.searchParams.append('referral', auth.user.referral)
  }

  return {
    title: data.value?.title,
    url: path.toString(),
    media: getMediaPath(data.value?.media, 'default-optimized'),
  }
})

onBeforeRouteUpdate(() => {
  refetch.value()
})

function store(model) {
  return new Promise((resolve, reject) => {
    http(`/protocols/${data.value.form_id}`, {
      method: 'POST',
      body: {
        ...model,
      },
    })
      .then(() => {
        userHasProtocol.value = true
        notify({
          group: 'default',
          type: 'success',
          title: 'Голос зачислен',
          text: 'Продолжайте участвовать в опросах и голосованиях, чтобы сделать наш регион лучше',
        })
        resolve()
      })
      .catch((e) => {
        if (e.data?.message) {
          notify({
            group: 'default',
            type: 'error',
            title: 'Ошибка',
            text: e.data?.message,
          })
        }

        reject()
      })
  })
}

watch(
  () => auth.user,
  () => {
    refetch.value()
  },
)
</script>
